import { ChangeEvent, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { BoardStatus, boardState, setStatus } from 'common/modules/app/board';
import { GlossaryState } from 'common/modules/app/glossaries';
import { FetchState, FetchStatus } from 'common/modules/app/fetch';
import { loadCookie, SasCookieKeys } from 'common/utils/cookie-utils';
import { BoardGL, Glossary } from 'common/utils/glossary';
import {
  changeSetting,
  postDevicesActivationReserve,
  PaidFeaturesInfo,
  StateType,
  GET_DEVICES_ACTIVATION_ID,
  POST_DEVICES_ACTIVATION_RESERVE_ID,
  GET_DEVICES_ACTIVATION_OUTPUT_ID,
  POST_DEVICES_ACTIVATION_RESERVE_CANCEL_ID,
  DeviceActivateInitialState,
  setReqFileDataList,
  setFileLoadingErrorData,
  FileLoadingErrorInfo,
  setApiErrorMessage,
  DialogType,
  setDialogMode
} from 'common/modules/domain/deviceActivate';
import { UserInformation } from 'common/modules/domain/user';
import { AppState } from 'pages';
import { DeviceActivateBoard } from 'pages/board/components/DeviceActivateBoard';

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const boardState = useSelector<AppState, boardState>(
    (state) => state.app.board
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const deviceActivateState = useSelector<AppState, DeviceActivateInitialState>(
    (state) => state.domain.deviceActivate
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );

  const isDisplaySub =
    boardState.status ===
      BoardStatus.DEVICE_ACTIVATE_DOWNLOAD_CONFIRMATION_DISP ||
    boardState.status === BoardStatus.DEVICE_ACTIVATE_UPLOAD_RESULT_DISP ||
    boardState.status ===
      BoardStatus.DEVICE_ACTIVATE_API_ERROR_COMPLETION_DISP ||
    boardState.status === BoardStatus.DEVICE_ACTIVATE_FILE_LOADING_ERROR_DISP ||
    boardState.status === BoardStatus.DEVICE_ACTIVATE_DELETE_CONFIRMATION_DISP;

  // 有償機能アクティベート情報一覧APIの実行状態
  const isDeviceActivateListProcessing =
    fetchState[GET_DEVICES_ACTIVATION_ID] !== null &&
    fetchState[GET_DEVICES_ACTIVATION_ID] === FetchStatus.PROCESSING;
  const isDeviceActivateListProcessingFail =
    fetchState[GET_DEVICES_ACTIVATION_ID] !== null &&
    fetchState[GET_DEVICES_ACTIVATION_ID] === FetchStatus.FAILED;
  const isDeviceActivateListProcessingUntreated =
    fetchState[GET_DEVICES_ACTIVATION_ID] !== null &&
    fetchState[GET_DEVICES_ACTIVATION_ID] === FetchStatus.UNTREATED;

  // 有償機能予約APIの実行状態
  const isDeviceActivateReserveProcessing =
    fetchState[POST_DEVICES_ACTIVATION_RESERVE_ID] !== null &&
    fetchState[POST_DEVICES_ACTIVATION_RESERVE_ID] === FetchStatus.PROCESSING;

  // 有償機能確定APIの実行状態
  const isDeviceActivateOutputProcessing =
    fetchState[GET_DEVICES_ACTIVATION_OUTPUT_ID] !== null &&
    fetchState[GET_DEVICES_ACTIVATION_OUTPUT_ID] === FetchStatus.PROCESSING;

  // 有償機能予約取り消しAPIの実行状態
  const isDeviceActivateReserveCancelProcessing =
    fetchState[POST_DEVICES_ACTIVATION_RESERVE_CANCEL_ID] !== null &&
    fetchState[POST_DEVICES_ACTIVATION_RESERVE_CANCEL_ID] ===
      FetchStatus.PROCESSING;

  // API実行中か否か
  const isProcessing =
    isDeviceActivateListProcessing ||
    isDeviceActivateReserveProcessing ||
    isDeviceActivateOutputProcessing ||
    isDeviceActivateReserveCancelProcessing;

  return {
    board: boardState,
    user: userState,
    glossary: Glossary.instance(glState),
    isDisplay: boardState.status === BoardStatus.DEVICE_ACTIVATE_DISP,
    isDisplaySub: isDisplaySub,
    isProcessing: isProcessing,
    isDeviceActivateListProcessingFail: isDeviceActivateListProcessingFail,
    isDeviceActivateListProcessingUntreated: isDeviceActivateListProcessingUntreated,
    deviceActivate: deviceActivateState,
    isDeviceActivateReserveCancelProcessing: isDeviceActivateReserveCancelProcessing
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
/**
 * StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const cookie = loadCookie();
  const _token = cookie[SasCookieKeys.KEY_TOKEN];
  const _refreshToken = cookie[SasCookieKeys.KEY_REFRESH_TOKEN];
  const _board = state.board;
  const _deviceActivate = state.deviceActivate;
  const _language = state.user.language;
  const _dialogMode = state.deviceActivate.dialogMode;
  /** 選択中のユーザー注文番号に紐づく有償機能情報を取得 */
  const paidFeaturesInfo: PaidFeaturesInfo =
    _deviceActivate.deviceActivateMap[_board.selectedPurchaseIdForTerminal];
  /** 有償機能アクティベート情報リストマップを作成 */
  const _deviceActivateMap: {
    [key: string]: {
      /** モデル名 */
      modelName: string;
      /** 状態("active"：有効 or "reserve"：予約) */
      status: StateType;
      /** 機種ID */
      modelId: string;
      /** シリアル番号 */
      serialNo: string;
      /** アップロード日時 (ISO8601 UTC) */
      uploadAt: string;
      /** 削除チェックボックスの状態(true or false) */
      isDelete: boolean;
    };
  } = {};
  if (paidFeaturesInfo != null) {
    if (paidFeaturesInfo.activates != null) {
      for (const terminalId of Object.keys(paidFeaturesInfo.activates)) {
        const activate = paidFeaturesInfo.activates[terminalId];
        _deviceActivateMap[terminalId] = {
          modelName: activate.deviceActivateInfo.modelName,
          status: activate.deviceActivateInfo.status,
          modelId: activate.deviceActivateInfo.modelId,
          serialNo: activate.deviceActivateInfo.serialNo,
          uploadAt: activate.deviceActivateInfo.uploadAt,
          isDelete: activate.isDelete
        };
      }
    }
  }

  // 購入情報から取り出す値の初期値を設定
  let planLimit = 0;
  let planUsage = 0;
  let confirmAt = '';
  // 購入情報が取得出来ていれば、取得した購入情報の値で更新
  if (paidFeaturesInfo != null) {
    planLimit = paidFeaturesInfo.limit ? paidFeaturesInfo.limit : 0;
    planUsage = paidFeaturesInfo.usage ? paidFeaturesInfo.usage : 0;
    confirmAt = paidFeaturesInfo.confirmAt ? paidFeaturesInfo.confirmAt : '';
  }

  /**
   * リクエストファイルのアップロードボタンをクリックした後に、
   * ファイル選択後に動作する処理
   */
  const onClickUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    // 選択したファイルを取得
    const selectFiles = e.target.files;
    // ファイルが選択されていなければ処理を抜ける
    if (!selectFiles) return;
    // ファイルの選択数が200以上の場合はアラートを表示して処理を抜ける
    if (selectFiles.length > 200) {
      dispatch(
        setApiErrorMessage(state.glossary.glx(BoardGL.OverSelectedFiles))
      );
      dispatch(
        setStatus(BoardStatus.DEVICE_ACTIVATE_API_ERROR_COMPLETION_DISP)
      );
      e.target.value = '';
      return;
    }
    // 選択されたファイルの読み込み処理
    const promises = Array.from(selectFiles).map((file: File) => {
      return new Promise<any>((resolve, reject) => {
        if (!file.name.toLowerCase().endsWith('.dat')) {
          reject({
            fileName: file.name,
            message: state.glossary.glx(BoardGL.ReqFileExtensionFailure)
          });
          e.target.value = '';
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const fileText: string = e.target ? String(e.target.result) : '';
            const parsedData = JSON.parse(fileText);
            // 必須項目のチェック
            if (
              'header' in parsedData &&
              parsedData['header'] !== null &&
              parsedData['header'] !== undefined &&
              '1stData' in parsedData &&
              parsedData['1stData'] !== null &&
              parsedData['1stData'] !== undefined &&
              '2ndData' in parsedData &&
              parsedData['2ndData'] !== null &&
              parsedData['2ndData'] !== undefined
            ) {
              if (
                'reqVersion' in parsedData['header'] &&
                parsedData['header']['reqVersion'] !== null &&
                parsedData['header']['reqVersion'] !== undefined &&
                'firmwareVersion' in parsedData['header'] &&
                parsedData['header']['firmwareVersion'] !== null &&
                parsedData['header']['firmwareVersion'] !== undefined &&
                'modelName' in parsedData['header'] &&
                parsedData['header']['modelName'] !== null &&
                parsedData['header']['modelName'] !== undefined &&
                '1stId' in parsedData['header'] &&
                parsedData['header']['1stId'] !== null &&
                parsedData['header']['1stId'] !== undefined
              ) {
                // 必須項目自体はある
                resolve({ name: file.name, data: parsedData });
              } else {
                // headerの中の項目でキー自体が存在しないか値がnullかundefinedの場合
                reject({
                  fileName: file.name,
                  message: state.glossary.glx(
                    BoardGL.ReqFileNecessaryNotContainedFailure
                  )
                });
              }
            } else {
              // header,1stData,2ndDataの中でキー自体が存在しないか値がnullかundefinedの場合
              reject({
                fileName: file.name,
                message: state.glossary.glx(
                  BoardGL.ReqFileNecessaryNotContainedFailure
                )
              });
            }
          } catch (error) {
            // 読み込み失敗(JSON構文エラー)
            reject({
              fileName: file.name,
              message: state.glossary.glx(BoardGL.ReqFileLoadingFailure)
            });
          }
        };
        reader.onerror = () =>
          // ファイル読み込み自体の失敗(ファイル破損、アクセス制限など)
          reject({
            fileName: file.name,
            message: state.glossary.glx(BoardGL.ReqFileLoadingFailure)
          });
        reader.readAsText(file);
      });
    });
    // ファイル読み込み後の処理
    Promise.allSettled(promises).then((results) => {
      // ファイル読み込みに成功した結果を取得してコンソールに出力
      const successfulData = [];
      const errors: FileLoadingErrorInfo[] = [];
      for (const result of results) {
        if (result.status === 'fulfilled') {
          successfulData.push({
            reqVersion: result.value['data']['header']['reqVersion'],
            firmwareVersion: result.value['data']['header']['firmwareVersion'],
            modelName: result.value['data']['header']['modelName'],
            keyId: result.value['data']['header']['1stId'],
            data: result.value['data']['1stData']
          });
        } else {
          errors.push(result.reason);
        }
      }
      // ファイル読み込みに失敗した結果が存在する場合はアラート表示して処理を抜ける
      // ※エラー処理については製品開発時に対応する
      if (errors.length > 0) {
        dispatch(setFileLoadingErrorData(errors));
        dispatch(
          setStatus(BoardStatus.DEVICE_ACTIVATE_FILE_LOADING_ERROR_DISP)
        );
        e.target.value = '';
        return;
      }
      // ファイル読み込みに成功した結果をstateに保持
      dispatch(setReqFileDataList(successfulData as []));
      // APIを実行する
      const purchaseId = _board.selectedPurchaseIdForTerminal;
      // 有償ファームウェアのアクティベート予約API実行(DryRunモード)
      dispatch(
        postDevicesActivationReserve(
          _token,
          _refreshToken,
          purchaseId,
          successfulData as [],
          state.glossary,
          _language,
          false,
          true
        )
      );
    });
    e.target.value = '';
  };

  /** アクティベーションファイルのダウンロードボタンをクリックした際の処理 */
  const onClickDownload = () => {
    dispatch(setStatus(BoardStatus.DEVICE_ACTIVATE_DOWNLOAD_CONFIRMATION_DISP));
  };

  /** クローズボタンをクリックした際の処理 */
  const onClickClose = () => {
    // 処理中はダイアログを閉じれない
    if (!state.isProcessing) {
      dispatch(setStatus(BoardStatus.NOT_DISP));
    }
  };

  /** 削除ボタンをクリックした際の処理 */
  const onClickDelete = () => {
    dispatch(setStatus(BoardStatus.DEVICE_ACTIVATE_DELETE_CONFIRMATION_DISP));
  };

  /** 削除対象フラグ更新処理 */
  const updateDelete = (terminalId: string, isDelete: boolean) => {
    dispatch(
      changeSetting(_board.selectedPurchaseIdForTerminal, terminalId, isDelete)
    );
  };

  /** 選択ボタンをクリックした際の処理 */
  const onClickSelect = () => {
    if (_dialogMode === DialogType.NORMAL) {
      dispatch(setDialogMode(DialogType.DELETE));
    } else {
      dispatch(setDialogMode(DialogType.NORMAL));
    }
    // チェックボックスを未選択状態にする
    for (const value of Object.values(_deviceActivateMap)) {
      if (value.status === StateType.TYPE_RESERVE) {
        const terminal_id = `${value.modelId}${value.serialNo}`;
        updateDelete(terminal_id, false);
      }
    }
  };

  return {
    isDisplay: state.isDisplay,
    isDisplaySub: state.isDisplaySub,
    glossary: state.glossary,
    language: _language,
    planLimit: planLimit,
    planUsage: planUsage,
    confirmAt: confirmAt,
    isProcessing: state.isProcessing,
    isDeviceActivateListProcessingFail:
      state.isDeviceActivateListProcessingFail,
    isDeviceActivateListProcessingUntreated:
      state.isDeviceActivateListProcessingUntreated,
    deviceActivateMap: _deviceActivateMap,
    dialogMode: _dialogMode,
    onClickUpload: onClickUpload,
    onClickDownload: onClickDownload,
    onClickClose: onClickClose,
    onClickDelete: onClickDelete,
    updateDelete: updateDelete,
    onClickSelect: onClickSelect
  };
};

/**
 * 有償機能アクティベート画面全体のContainer Component
 */
export default function ContainerComponent(ownProps: { isMobile?: boolean }) {
  const _props = { ...useStateProps(), ...useMargeProps(useStateProps()) };
  return <DeviceActivateBoard {...{ ..._props, ...ownProps }} />;
}
